import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PillTabItem } from 'src/app/shared/components/pill-tabs/pill-tabs.component';

type CalendarItemType = 'event' | 'news' | 'task' | 'update';
type CalendarFilter = 'all' | CalendarItemType;

interface CalendarDay {
  date: Date | null;
  dayNumber: number | null;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  hasEvents: boolean;
  hasNews: boolean;
  hasTasks: boolean;
  hasUpdates: boolean;
}

interface CalendarItem {
  type: CalendarItemType;
  title: string;
  date: Date;
  source: any;
  badge: string;
  actionLabel: string;
  assigneeInitials: string;
}

@Component({
  selector: 'app-dashboard-calendar-widget',
  templateUrl: './dashboard-calendar-widget.component.html',
  styleUrls: ['./dashboard-calendar-widget.component.scss'],
})
export class DashboardCalendarWidgetComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() events: any[] = [];
  @Input() news: any[] = [];
  @Input() tasks: any[] = [];
  @Input() updates: any[] = [];
  @Input() eventsLoaded = false;
  @Input() newsLoaded = false;
  @Input() tasksLoaded = false;
  @Input() updatesLoaded = false;
  @Input() eventsError = false;
  @Input() newsError = false;
  @Input() tasksError = false;
  @Input() updatesError = false;
  @Input() languageCode = 'en';

  @Output() eventSelected = new EventEmitter<any>();
  @Output() newsSelected = new EventEmitter<any>();
  @Output() taskSelected = new EventEmitter<any>();
  @Output() updateSelected = new EventEmitter<any>();
  @Output() retryRequested = new EventEmitter<void>();

  readonly filterKeys: CalendarFilter[] = [
    'all',
    'event',
    'news',
    'task',
    'update',
  ];

  get filters(): Array<{ key: CalendarFilter; label: string }> {
    return this.filterKeys.map(key => ({
      key,
      label: this.t(`filters.${key}`),
    }));
  }

  get weekDays(): string[] {
    const formatter = new Intl.DateTimeFormat(this.locale, {
      weekday: 'short',
    });
    return Array.from({ length: 7 }, (_, index) => {
      // Use a local midday sample date so weekday labels don't drift across time zones.
      const date = new Date(2024, 0, 1 + index, 12);
      return formatter.format(date);
    });
  }

  activeView: 'upcoming' | 'scheduled' = 'upcoming';
  activeFilterIndex = 0;
  filterTabs: PillTabItem[] = [];
  visibleMonth = this.startOfMonth(new Date());
  selectedDate: Date | null = null;
  calendarDays: CalendarDay[] = [];
  calendarItems: CalendarItem[] = [];
  selectedItems: CalendarItem[] = [];
  hasScrollableAgenda = false;
  scrollThumbHeight = 100;
  scrollThumbTop = 0;

  private monthPanel?: ElementRef<HTMLElement>;
  private agendaList?: ElementRef<HTMLElement>;
  private monthResizeObserver?: ResizeObserver;
  private agendaResizeObserver?: ResizeObserver;
  private heightUpdateFrame: number | null = null;
  private scrollUpdateFrame: number | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private hostRef: ElementRef<HTMLElement>,
    private translate: TranslateService
  ) {
    const refresh = () => {
      this.calendarItems = this.buildCalendarItems();
      this.rebuildView();
    };

    // `instant()` returns the key when the JSON hasn't loaded yet. Refresh the
    // derived copy when translations become available.
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(refresh);
    this.translate.onTranslationChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(refresh);
    this.translate.onDefaultLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(refresh);
  }

  @ViewChild('monthPanel')
  set monthPanelRef(panel: ElementRef<HTMLElement> | undefined) {
    this.monthPanel = panel;
    this.monthResizeObserver?.disconnect();

    if (panel && typeof ResizeObserver !== 'undefined') {
      this.monthResizeObserver = new ResizeObserver(() =>
        this.queueAgendaPanelHeightUpdate()
      );
      this.monthResizeObserver.observe(panel.nativeElement);
    }

    this.queueAgendaPanelHeightUpdate();
  }

  @ViewChild('agendaList')
  set agendaListRef(list: ElementRef<HTMLElement> | undefined) {
    this.agendaList = list;
    this.agendaResizeObserver?.disconnect();

    if (list && typeof ResizeObserver !== 'undefined') {
      this.agendaResizeObserver = new ResizeObserver(() =>
        this.queueScrollIndicatorUpdate()
      );
      this.agendaResizeObserver.observe(list.nativeElement);
    }

    this.queueScrollIndicatorUpdate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['events'] ||
      changes['news'] ||
      changes['tasks'] ||
      changes['updates'] ||
      changes['eventsLoaded'] ||
      changes['newsLoaded'] ||
      changes['tasksLoaded'] ||
      changes['updatesLoaded'] ||
      changes['languageCode']
    ) {
      this.calendarItems = this.buildCalendarItems();
      this.ensureUsefulSelection();
      this.rebuildView();
      this.queueAgendaPanelHeightUpdate();
    }
  }

  ngAfterViewInit(): void {
    this.queueAgendaPanelHeightUpdate();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.monthResizeObserver?.disconnect();
    this.agendaResizeObserver?.disconnect();
    if (this.heightUpdateFrame !== null) {
      cancelAnimationFrame(this.heightUpdateFrame);
    }
    if (this.scrollUpdateFrame !== null) {
      cancelAnimationFrame(this.scrollUpdateFrame);
    }
  }

  get isLoading(): boolean {
    return (
      !this.eventsLoaded ||
      !this.newsLoaded ||
      !this.tasksLoaded ||
      !this.updatesLoaded
    );
  }

  get hasError(): boolean {
    return this.eventsError || this.newsError || this.tasksError || this.updatesError;
  }

  get showFullErrorState(): boolean {
    return this.hasError && this.calendarItems.length === 0;
  }

  get showInlineErrorState(): boolean {
    return this.hasError && this.calendarItems.length > 0;
  }

  get monthLabel(): string {
    return new Intl.DateTimeFormat(this.locale, {
      month: 'long',
      year: 'numeric',
    }).format(this.visibleMonth);
  }

  get headerDateLabel(): string {
    if (this.selectedDate) {
      return this.formatHeaderDate(this.selectedDate);
    }

    const today = new Date();
    if (this.isSameMonth(this.visibleMonth, today)) {
      return this.formatHeaderDate(today);
    }

    return this.monthLabel;
  }

  get selectedDateLabel(): string {
    if (!this.selectedDate) {
      return this.t('allEvents');
    }
    return new Intl.DateTimeFormat(this.locale, {
      weekday: 'long',
      day: '2-digit',
      month: 'short',
    }).format(this.selectedDate);
  }

  get locale(): string {
    return this.languageCode === 'de' ? 'de-DE' : 'en-US';
  }

  get scheduledCount(): number {
    return this.calendarItems.length;
  }

  get visibleAgendaItems(): CalendarItem[] {
    return this.activeView === 'scheduled'
      ? this.calendarItems
      : this.selectedItems;
  }

  get emptyAgendaLabel(): string {
    return this.activeView === 'scheduled'
      ? this.t('emptyScheduled')
      : this.t('emptyDay');
  }

  setActiveView(view: 'upcoming' | 'scheduled'): void {
    if (this.activeView === view) {
      return;
    }

    this.activeView = view;
    this.queueAgendaPanelHeightUpdate();
    this.queueScrollIndicatorUpdate();
  }

  goToToday(): void {
    this.selectedDate = this.startOfDay(new Date());
    this.visibleMonth = this.startOfMonth(this.selectedDate);
    this.rebuildView();
  }

  changeMonth(offset: number): void {
    this.visibleMonth = new Date(
      this.visibleMonth.getFullYear(),
      this.visibleMonth.getMonth() + offset,
      1
    );
    this.rebuildView();
  }

  selectDate(day: CalendarDay): void {
    if (!day.date) return;

    if (this.selectedDate && this.isSameDay(day.date, this.selectedDate)) {
      this.selectedDate = null;
    } else {
      this.selectedDate = this.startOfDay(day.date);
    }

    if (!day.isCurrentMonth) {
      this.visibleMonth = this.startOfMonth(day.date);
    }
    this.rebuildView();
  }

  clearSelection(): void {
    this.selectedDate = null;
    this.rebuildView();
  }

  onFilterTabChange(index: number): void {
    this.activeFilterIndex = index;
    this.rebuildSelectedItems();
  }

  itemCount(filter: CalendarFilter): number {
    return this.itemsForSelectedDate().filter(item =>
      filter === 'all' ? true : item.type === filter
    ).length;
  }

  openItem(item: CalendarItem): void {
    if (item.type === 'event') {
      this.eventSelected.emit(item.source);
      return;
    }

    if (item.type === 'news') {
      this.newsSelected.emit(item.source);
      return;
    }

    if (item.type === 'update') {
      this.updateSelected.emit(item.source);
      return;
    }

    this.taskSelected.emit(item.source);
  }

  formatTime(item: CalendarItem): string {
    const hasClockTime =
      item.date.getHours() !== 0 || item.date.getMinutes() !== 0;

    if (hasClockTime && item.type !== 'task') {
      return new Intl.DateTimeFormat(this.locale, {
        hour: '2-digit',
        minute: '2-digit',
      }).format(item.date);
    }

    return new Intl.DateTimeFormat(this.locale, {
      day: 'numeric',
      month: 'short',
    }).format(item.date);
  }

  itemActionLabel(item: CalendarItem): string {
    const daysLeft = this.daysUntil(item.date);
    if (item.type === 'task' && daysLeft !== null && daysLeft <= 14) {
      if (daysLeft < 0) return this.t('actions.overdue');
      if (daysLeft === 0) return this.t('actions.dueToday');
      if (daysLeft === 1) return this.t('actions.oneDayLeft');
      return this.t('actions.daysLeft', { count: daysLeft });
    }

    return item.actionLabel;
  }

  isUrgentItem(item: CalendarItem): boolean {
    const daysLeft = this.daysUntil(item.date);
    return item.type === 'task' && daysLeft !== null && daysLeft <= 14;
  }

  onAgendaScroll(): void {
    this.queueScrollIndicatorUpdate();
  }

  private rebuildView(): void {
    this.calendarDays = this.buildCalendarDays();
    this.filterTabs = this.buildFilterTabs();
    this.rebuildSelectedItems();
  }

  private get activeFilter(): CalendarFilter {
    return this.filters[this.activeFilterIndex]?.key ?? 'all';
  }

  private buildFilterTabs(): PillTabItem[] {
    return this.filterKeys.map(key => {
      const count = this.itemCount(key);
      return {
        label: this.t(`filters.${key}`),
        count: key === 'all' || count === 0 ? undefined : count,
      };
    });
  }

  private queueAgendaPanelHeightUpdate(): void {
    if (typeof requestAnimationFrame === 'undefined') {
      this.updateAgendaPanelHeight();
      return;
    }

    if (this.heightUpdateFrame !== null) {
      cancelAnimationFrame(this.heightUpdateFrame);
    }

    this.heightUpdateFrame = requestAnimationFrame(() => {
      this.heightUpdateFrame = null;
      this.updateAgendaPanelHeight();
    });
  }

  private updateAgendaPanelHeight(): void {
    const agendaTop =
      this.agendaList?.nativeElement.getBoundingClientRect().top
      ?? this.hostRef.nativeElement.getBoundingClientRect().top;
    const viewportHeight =
      typeof window !== 'undefined' ? window.innerHeight : 0;
    const scheduledHeight = Math.max(280, viewportHeight - agendaTop - 24);

    this.hostRef.nativeElement.style.setProperty(
      '--calendar-agenda-list-height',
      `${scheduledHeight}px`
    );

    const height = this.monthPanel?.nativeElement.offsetHeight;

    if (!height) {
      this.hostRef.nativeElement.style.removeProperty(
        '--calendar-panel-height'
      );
      return;
    }

    this.hostRef.nativeElement.style.setProperty(
      '--calendar-panel-height',
      `${height}px`
    );
  }

  private rebuildSelectedItems(): void {
    this.selectedItems = this.itemsForSelectedDate()
      .filter(item =>
        this.activeFilter === 'all' ? true : item.type === this.activeFilter
      )
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    this.queueScrollIndicatorUpdate();
  }

  private queueScrollIndicatorUpdate(): void {
    if (typeof requestAnimationFrame === 'undefined') {
      this.updateScrollIndicator();
      return;
    }

    if (this.scrollUpdateFrame !== null) {
      cancelAnimationFrame(this.scrollUpdateFrame);
    }

    this.scrollUpdateFrame = requestAnimationFrame(() => {
      this.scrollUpdateFrame = null;
      this.updateScrollIndicator();
    });
  }

  private updateScrollIndicator(): void {
    const list = this.agendaList?.nativeElement;

    if (!list) {
      this.hasScrollableAgenda = false;
      this.scrollThumbHeight = 100;
      this.scrollThumbTop = 0;
      return;
    }

    const scrollableDistance = list.scrollHeight - list.clientHeight;
    this.hasScrollableAgenda = scrollableDistance > 1;

    if (!this.hasScrollableAgenda) {
      this.scrollThumbHeight = 100;
      this.scrollThumbTop = 0;
      return;
    }

    this.scrollThumbHeight = Math.min(
      100,
      Math.max(10, (list.clientHeight / list.scrollHeight) * 100)
    );
    this.scrollThumbTop =
      (list.scrollTop / scrollableDistance) * (100 - this.scrollThumbHeight);
  }

  private itemsForSelectedDate(): CalendarItem[] {
    if (!this.selectedDate) {
      return this.calendarItems;
    }
    return this.calendarItems.filter(item =>
      this.selectedDate && this.isSameDay(item.date, this.selectedDate)
    );
  }

  private buildCalendarItems(): CalendarItem[] {
    const events = Array.isArray(this.events) ? this.events : [];
    const news = Array.isArray(this.news) ? this.news : [];
    const tasks = Array.isArray(this.tasks) ? this.tasks : [];
    const updates = Array.isArray(this.updates) ? this.updates : [];

    const eventItems = events
      .map(event => this.createItem(event, 'event', event?.start))
      .filter((item): item is CalendarItem => !!item);

    const newsItems = news
      .map(newsItem => this.createItem(newsItem, 'news', newsItem?.dateTime))
      .filter((item): item is CalendarItem => !!item);

    const taskItems = tasks
      .map(task => this.createItem(task, 'task', task?.dueAt))
      .filter((item): item is CalendarItem => !!item);

    const updateItems = updates
      .map(update => this.createItem(update, 'update', update?.date))
      .filter((item): item is CalendarItem => !!item);

    return [...eventItems, ...newsItems, ...taskItems, ...updateItems].sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    );
  }

  private createItem(
    source: any,
    type: CalendarItemType,
    dateValue: string | Date | undefined
  ): CalendarItem | null {
    if (!dateValue) return null;

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;

    const title =
      type === 'task'
        ? source?.name || source?.title || ''
        : type === 'update'
          ? source?.displayTitle || source?.title || ''
          : source?.title || '';

    return {
      type,
      title,
      date,
      source,
      assigneeInitials: this.firstAssigneeInitials(source, title),
      badge: this.t(`badges.${type}`),
      actionLabel:
        type === 'event'
          ? this.t('actions.addToCalendar')
          : type === 'news'
            ? this.t('actions.open')
            : type === 'task'
              ? this.t('actions.openTask')
              : this.t('actions.view'),
    };
  }

  private ensureUsefulSelection(): void {
    // By default, no specific date is selected so all items are shown.
  }

  private buildCalendarDays(): CalendarDay[] {
    const firstOfMonth = this.startOfMonth(this.visibleMonth);
    const firstWeekday = (firstOfMonth.getDay() + 6) % 7;
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(firstOfMonth.getDate() - firstWeekday);

    const lastOfMonth = new Date(
      this.visibleMonth.getFullYear(),
      this.visibleMonth.getMonth() + 1,
      0
    );
    const lastWeekday = (lastOfMonth.getDay() + 6) % 7;
    const gridEnd = new Date(lastOfMonth);
    gridEnd.setDate(lastOfMonth.getDate() + (6 - lastWeekday));
    const dayCount =
      Math.round(
        (gridEnd.getTime() - gridStart.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1;

    return Array.from({ length: dayCount }, (_, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      const dayItems = this.calendarItems.filter(item =>
        this.isSameDay(item.date, date)
      );

      return {
        date,
        dayNumber: date.getDate(),
        isCurrentMonth: date.getMonth() === this.visibleMonth.getMonth(),
        isToday: this.isSameDay(date, new Date()),
        isSelected: !!this.selectedDate && this.isSameDay(date, this.selectedDate),
        hasEvents: dayItems.some(item => item.type === 'event'),
        hasNews: dayItems.some(item => item.type === 'news'),
        hasTasks: dayItems.some(item => item.type === 'task'),
        hasUpdates: dayItems.some(item => item.type === 'update'),
      };
    });
  }

  private formatHeaderDate(date: Date): string {
    const month = new Intl.DateTimeFormat(this.locale, {
      month: 'long',
    }).format(date);
    const formatted =
      this.locale === 'de-DE'
        ? new Intl.DateTimeFormat(this.locale, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          }).format(date)
        : `${date.getDate()} ${month}, ${date.getFullYear()}`;

    return this.isSameDay(this.startOfDay(date), this.startOfDay(new Date()))
      ? this.t('today', { date: formatted })
      : formatted;
  }

  private daysUntil(date: Date): number | null {
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    const today = this.startOfDay(new Date());
    const due = this.startOfDay(date);
    return Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }

  private startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  private firstAssigneeInitials(source: any, title: string): string {
    const assignee = this.firstAssignee(source);
    return (
      this.personInitials(assignee) ||
      this.nameInitials(title) ||
      '?'
    );
  }

  private firstAssignee(source: any): any | null {
    const list =
      source?.assignees ||
      source?.attendees ||
      source?.participants ||
      [];

    if (Array.isArray(list) && list.length) {
      return list[0];
    }

    return (
      source?.assignee ||
      source?.organizer ||
      source?.createdBy ||
      source?.owner ||
      null
    );
  }

  private personInitials(person: any): string {
    if (!person) {
      return '';
    }

    const fromName = this.nameInitials(
      `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() ||
        person.name ||
        person.displayName ||
        person.fullName ||
        ''
    );
    if (fromName) {
      return fromName;
    }

    const email = String(person.email || '').trim();
    const localPart = email.split('@')[0];
    return this.nameInitials(localPart.replace(/[._-]+/g, ' '));
  }

  private nameInitials(value: string): string {
    const parts = String(value || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }

    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return '';
  }

  private t(key: string, params?: Record<string, string | number>): string {
    return this.translate.instant(
      `translations.dashboard.calendarWidget.${key}`,
      params
    );
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private isSameDay(first: Date, second: Date): boolean {
    return (
      first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate()
    );
  }

  private isSameMonth(first: Date, second: Date): boolean {
    return (
      first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth()
    );
  }
}
